import React, { useEffect, useState } from "react";
import { ExternalLink, Sparkles } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const SPOTLIGHTS = [
  { partner: "iolstar", title: "Easy-inject preloaded aspheric IOL", text: "Fully preloaded delivery system with an aberration-neutral optic in glistening-free hydrophobic acrylic." },
  { partner: "cristalens", title: "Artis Symbiose® — Mid & Plus", text: "Complementary-phase binocular EDOF: continuous vision from 40 cm to infinity, toric with PCA ready." },
  { partner: "mikajaki", title: "EyeLib™ automated diagnostics", text: "Robotic, AI-driven capture of refraction, topography and biometry in one self-guided station." },
  { partner: "iolstar", title: "Multifocal & EDOF range", text: "A new approach to EDOF optics — focus on both far and near with one preloaded platform." },
  { partner: "cristalens", title: "Made in France since 1994", text: "Design and manufacturing of hydrophobic & hydrophilic IOLs under one roof in Lannion, Brittany." },
  { partner: "mikajaki", title: "Smarter, faster clinics", text: "Cut pre-consultation time with autonomous eye exams and AI triage that hands clean data to the surgeon." },
];

const ROTATE_MS = 6000;
const PROGRESS_ANIM = { initial: { width: "0%" }, animate: { width: "100%" }, transition: { duration: ROTATE_MS / 1000, ease: "linear" } };
const FADE_ANIM = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -8 }, transition: { duration: 0.35 } };

const PARTNERS = [
  {
    id: "iolstar",
    name: "IOL Star",
    logo: "/sponsors/iolstar.svg",
    url: "https://www.iolstar.com/",
    tagline: "Preloaded aspheric, multifocal & EDOF IOLs in glistening-free hydrophobic acrylic",
    accent: "from-amber-400/70 to-orange-500/70",
    logoClass: "h-8",
  },
  {
    id: "cristalens",
    name: "Cristalens International",
    logo: "/sponsors/cristalens.png",
    url: "https://cristalens-international.com/",
    tagline: "French intraocular lens manufacturer — Artis platform, made in France",
    accent: "from-sky-400/70 to-blue-600/70",
    logoClass: "h-6",
  },
  {
    id: "mikajaki",
    name: "Mikajaki EyeLib",
    logo: "/sponsors/mikajaki.png",
    url: "https://mikajaki.ai/",
    tagline: "AI & robotics for faster, more accurate eye-health diagnostics",
    accent: "from-emerald-400/70 to-teal-600/70",
    logoClass: "h-10",
  },
];

export default function PartnerStrip() {
  return (
    <section data-testid="partner-strip" className="mt-2">
      <div className="flex items-center gap-3 mb-3">
        <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">Industry partners</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <PartnerSpotlight />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        {PARTNERS.map((p, i) => (
          <a
            key={p.id}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-testid={`partner-${p.id}`}
            className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-4 transition-transform hover:-translate-y-0.5 hover:shadow-md"
            style={{ animationDelay: `${i * 80}ms` }}
          >
            <span className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${p.accent}`} />
            <div className="shrink-0 w-28 h-14 grid place-items-center rounded-lg bg-slate-50 border border-slate-100 px-2">
              <img src={p.logo} alt={p.name} className={`${p.logoClass} w-auto max-w-full object-contain`} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                {p.name}
                <ExternalLink className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="text-xs text-slate-500 mt-0.5 leading-snug">{p.tagline}</div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

function PartnerSpotlight() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % SPOTLIGHTS.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [paused]);

  const spot = SPOTLIGHTS[idx];
  const partner = PARTNERS.find((p) => p.id === spot.partner);

  return (
    <div
      data-testid="partner-spotlight"
      data-partner={partner.id}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-950 text-white"
    >
      <span className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${partner.accent}`} />
      {!paused && (
        <motion.span
          key={idx}
          {...PROGRESS_ANIM}
          className="absolute bottom-0 left-0 h-0.5 bg-white/40"
          data-testid="spotlight-progress"
        />
      )}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 px-5 py-4">
        <div className="shrink-0 flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Spotlight
          </span>
          <div className="w-24 h-10 grid place-items-center rounded-md bg-white px-2">
            <img src={partner.logo} alt={partner.name} className="h-6 w-auto max-w-full object-contain" />
          </div>
        </div>
        <div className="min-w-0 h-14 sm:h-12 sm:flex-1 relative">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              {...FADE_ANIM}
              className="absolute inset-0"
            >
              <div className="text-sm font-semibold truncate" data-testid="spotlight-title">{spot.title}</div>
              <div className="text-xs text-slate-300 leading-snug line-clamp-2" data-testid="spotlight-text">{spot.text}</div>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center gap-1.5" data-testid="spotlight-dots">
            {SPOTLIGHTS.map((s, i) => (
              <button
                key={`${s.partner}-${s.title}`}
                aria-label={`Show spotlight ${i + 1}`}
                onClick={() => setIdx(i)}
                data-testid={`spotlight-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/35 hover:bg-white/60"}`}
              />
            ))}
          </div>
          <a
            href={partner.url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-testid="spotlight-link"
            className="inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 px-3.5 py-1.5 text-xs font-semibold hover:bg-slate-100 transition whitespace-nowrap"
          >
            Visit {partner.name.split(" ")[0]} <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
